<template>
  <v-row>
    <v-col cols="12">
      <ca-page-header :title="$t('pages.support.title')" icon="mdi-lifebuoy">
        <div class="text-body-2">
          <i18n path="pages.support.payments" tag="div">
            <template #finance_email>
              <a :href="`mailto:${financeEmail}`">{{ financeEmail }}</a>
            </template>
          </i18n>

          <div class="mt-2">
            <i>{{ $t("pages.support.spam") }}</i>
          </div>

          <div class="mt-2">
            {{ $t("pages.support.other") }}
          </div>

          <i18n path="pages.support.faq_notice" tag="div" class="mt-2">
            <template #faq>
              <router-link :to="{ name: 'faq' }" class="primary--text">
                {{ $t("pages.support.faq") }}
              </router-link>
            </template>
          </i18n>

          <div class="mt-2">
            {{ $t("pages.support.response_time") }}
          </div>
        </div>
      </ca-page-header>
    </v-col>

    <v-col cols="6" offset="3" sm="12" offset-sm="0" md="10" offset-md="1" lg="8" offset-lg="2">
      <div class="helpdesk-page">
        <helpdesk-rules v-if="messages.length === 0" class="mb-10"></helpdesk-rules>

        <div v-if="messages.length > 0" class="helpdesk-messages-container-wrap">
          <div id="chat" class="helpdesk-messages-container">
            <div v-for="message in messages" :key="message.id">
              <div class="helpdesk-message" :class="['helpdesk-message-' + message.sender_role]">
                <div class="helpdesk-message-header">
                  <span v-if="message.sender_role === 'user'" class="helpdesk-message-header-name">
                    {{ $t("pages.helpdesk.messages.you") }}
                    ({{ userEmail }})
                  </span>
                  <span v-else class="helpdesk-message-header-name">
                    {{ $t("pages.helpdesk.messages.support") }}
                  </span>
                  -
                  <span class="helpdesk-message-header-time">
                    {{ formatDate(message.created_at) }}
                  </span>
                </div>
                <div class="helpdesk-message-text">
                  <span v-if="message.text === '--autoanswer-subject-is-over--'">{{
                    $t("pages.helpdesk.messages.autoanswer_subject_is_over")
                  }}</span>
                  <span v-else>{{ message.text }}</span>
                </div>
                <div v-if="message.attachments.length > 0" class="helpdesk-message-attachments">
                  <div
                    v-for="attachment in message.attachments"
                    :key="attachment"
                    class="helpdesk-message-attachment"
                  >
                    <a :href="attachmentsUrl + message.id + '/' + attachment" target="_blank">{{ attachment }}</a>
                  </div>
                </div>
              </div>

              <div
                v-if="message.text === '--autoanswer-subject-is-over--'"
                class="mb-5"
                style="border-top: 1px solid #f0f1f4;"
              ></div>
            </div>
          </div>

          <div class="helpdesk-messages-note">
            {{ $t("pages.helpdesk.messages.note", { count: 100 }) }}
          </div>
        </div>

        <v-form @submit.prevent="handleSubmit">
          <v-textarea
            v-model="text"
            :label="$t('pages.helpdesk.inputs.message.placeholder')"
            :error-messages="errorText"
            outlined
          ></v-textarea>

          <div v-if="selectedFiles.length > 0" class="selected-files mb-6">
            {{ $t("pages.helpdesk.inputs.attachments.attached_files") }}
            <div v-for="(file, index) in selectedFiles" :key="index" class="selected-file">
              <span class="file-name">{{ file.name }}</span>
              <button type="button" class="remove-file ms-2" @click="removeFile(index)">
                &times;
              </button>
            </div>
          </div>

          <v-btn color="primary" class="helpdesk-btn-send" type="submit" :loading="sending" :disabled="text.trim() === ''">
            {{ $t("pages.helpdesk.btn.send") }}
          </v-btn>

          <div class="file-upload-container">
            <div class="file-upload-btn" @click="$refs.fileInput.click()">
              <input
                ref="fileInput"
                type="file"
                multiple
                name="file"
                class="drop-area__input"
                accept="image/png, image/jpeg, image/jpg"
                @change="handleFileSelect"
              />
              <i18n path="pages.helpdesk.inputs.attachments.btn" tag="div">
                <template #text>
                  <span class="helpdesk-message-file-upload-link font-weight-medium">
                    {{ $t("pages.helpdesk.inputs.attachments.attach_file") }}
                  </span>
                </template>
                <template #formats>
                  (png, jpg, jpeg)
                </template>
              </i18n>
            </div>
          </div>
        </v-form>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import CaPageHeader from "@/views/components/PageHeader";
import HelpdeskRules from "./Rules";
import { SUPPORT_FINANCE_EMAIL } from "@/constants/supportContacts";

export default {
  name: "SupportPage",
  components: { CaPageHeader, HelpdeskRules },
  metaInfo() {
    return {
      title: this.$t("pages.support.title")
    };
  },
  data() {
    return {
      messages: [],
      attachmentsUrl: "",
      sending: false,
      text: "",
      errorText: "",
      selectedFiles: [],
      chatContainer: null
    };
  },
  computed: {
    financeEmail() {
      return SUPPORT_FINANCE_EMAIL;
    },
    userEmail() {
      return this.$store.state.auth.userData.email;
    }
  },
  mounted() {
    this.loadMessages().then(() => {
      this.chatContainer = document.getElementById("chat");
      this.scrollChatToBottom();
    });
  },
  methods: {
    loadMessages() {
      return this.$store.dispatch("helpdesk/getUserMessages").then(result => {
        this.messages = result.messages;
        this.attachmentsUrl = result.attachmentsUrl;
      });
    },
    handleFileSelect(event) {
      const input = event.target;
      if (!input.files || !input.files.length) return;

      const newFiles = Array.from(input.files);
      this.selectedFiles = [...this.selectedFiles, ...newFiles];

      input.value = "";
    },
    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },
    handleSubmit() {
      const formattedText = this.text.trim();

      if (!formattedText) {
        return;
      }

      if (this.sending) {
        return;
      }

      this.errorText = "";
      this.sending = true;

      const formData = new FormData();
      formData.append("text", formattedText);

      this.selectedFiles.forEach(file => {
        formData.append("files[]", file);
      });

      this.$store
        .dispatch("helpdesk/addMessage", formData)
        .then(result => {
          this.messages.push(result.message);
          this.text = "";
          this.selectedFiles = [];

          this.$nextTick(() => {
            this.scrollChatToBottom();
          });
        })
        .catch(error => {
          const data = error.response ? error.response.data && error.response.data.data : null;
          const isNetworkError = error.code === "ERR_NETWORK" || error.message === "Network Error";

          if (isNetworkError) {
            this.errorText = this.$t("pages.helpdesk.errors.max_file_size_limit");
          } else if (data) {
            const err = typeof data === "object" ? Object.values(data).join(", ") : data;

            if (err === "invalid_file_type") {
              this.errorText = this.$t("pages.helpdesk.errors.invalid_file_type");
            } else if (err === "file_size_limit") {
              this.errorText = this.$t("pages.helpdesk.errors.max_file_size_limit");
            } else if (err === "filename_too_long") {
              this.errorText = this.$t("pages.helpdesk.errors.filename_too_long");
            } else if (err === "filename_invalid_chars") {
              this.errorText = this.$t("pages.helpdesk.errors.filename_invalid_chars");
            } else {
              this.errorText = err;
            }
          } else {
            this.errorText = this.$t("pages.helpdesk.errors.generic");
          }
        })
        .finally(() => {
          this.sending = false;
        });
    },
    scrollChatToBottom() {
      if (this.chatContainer === null) {
        return;
      }

      this.chatContainer.scrollTo({
        top: this.chatContainer.scrollHeight,
        behavior: "smooth"
      });
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const pad = n => (n < 10 ? "0" + n : "" + n);

      return (
        date.getDate() +
        " " +
        this.$t("app.months.short." + (date.getMonth() + 1)) +
        " " +
        date.getFullYear() +
        ", " +
        pad(date.getHours()) +
        ":" +
        pad(date.getMinutes())
      );
    }
  }
};
</script>

<style scoped lang="scss">
.helpdesk-page {
  position: relative;
  z-index: 1;

  .helpdesk-messages-container-wrap {
    margin-bottom: 40px;
  }

  .helpdesk-messages-container {
    border-radius: 4px;
    border: 1px #eae8e8 solid;
    max-height: 600px;
    overflow: auto;
    padding: 20px;
  }

  .helpdesk-messages-note {
    text-align: right;
    color: #908d8c;
    font-size: 0.8em;
  }

  .helpdesk-message {
    margin-bottom: 20px;
    background: #f0f1f4;
    padding: 8px 10px;
    border-radius: 8px;
  }

  .helpdesk-message-user {
    margin-left: 200px;
    background: #f0f0f0;
    border: 1px solid #ddd;
  }

  .helpdesk-message-support {
    margin-right: 200px;
    background: #fff;
    border: 1px solid #f0f1f4;
  }

  .helpdesk-message-text {
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  .helpdesk-message-header {
    margin-bottom: 4px;
  }

  .helpdesk-message-header-name {
    color: #ff6d00;
  }

  .helpdesk-message-file-upload-link {
    color: #ff6d00;
    cursor: pointer;
  }

  .helpdesk-message-attachment a {
    color: #ff6d00;
  }

  .selected-file {
    .remove-file,
    .file-name {
      color: #ff6d00;
    }
  }

  .file-upload-container {
    float: right;
  }

  .file-upload-btn {
    cursor: pointer;

    .drop-area__input {
      display: none;
    }
  }
}

@media (max-width: 960px) {
  .helpdesk-page {
    .helpdesk-btn-send {
      width: 100%;
      margin-bottom: 20px;
    }

    .file-upload-container {
      float: none;
      text-align: center;
    }

    .helpdesk-messages-container-wrap {
      margin-bottom: 20px;
    }

    .helpdesk-messages-container {
      border-radius: 0;
      border: none;
      padding: 0;
    }

    .helpdesk-message-user {
      margin-left: 0;
    }

    .helpdesk-message-support {
      margin-right: 0;
      background: #fff;
      border: 2px solid #f0f1f4;
    }

    .helpdesk-messages-note {
      text-align: left;
    }
  }
}
</style>
